import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import onHoverPopup from 'webapp/modifiers/on-hover-popup';
import ForecastsPopupsCorrelationTooltipComponent from 'webapp/components/forecasts/popups/correlation-tooltip';
import ForecastsPopupsRegressorCorrelationComponent from 'webapp/components/forecasts/popups/regressor-correlation';
import LoaderSvgComponent from 'webapp/components/loader-svg';

interface Args {
	correlationValue: number | null;
	isDisabled: boolean;
	isCalculating: boolean;
	onChange: (value: number) => void;
	onRecalculate: () => void;
}

export default class ForecastsDataCorrelationBadgeComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	get showBadge(): boolean {
		return !this.args.isCalculating && this.args.correlationValue !== null;
	}

	get strength() {
		if (this.args.correlationValue === null) {
			return 'weak';
		}

		const absValue = Math.abs(this.args.correlationValue);
		if (absValue > 0.7) return 'strong';
		if (absValue > 0.3) return 'moderate';

		return 'weak';
	}

	@action
	openCorrelationPopup(event: Event) {
		if (this.args.isDisabled || this.args.correlationValue === null) {
			return;
		}

		const element = event.target as HTMLElement;
		const popup = this.popupService.createContext(
			ForecastsPopupsRegressorCorrelationComponent,
			element,
		);
		popup.open({
			currentValue: this.args.correlationValue,
			onApply: (value: number) => {
				this.args.onChange(value);
			},
			onRecalculate: this.args.onRecalculate,
		});
	}

	@action
	openHoverPopup(element: HTMLElement) {
		if (this.args.isDisabled || this.args.correlationValue === null) {
			return;
		}

		const popup = this.popupService.createContext(
			ForecastsPopupsCorrelationTooltipComponent,
			element,
			{
				popupClass: 'popup-type-correlation-tooltip',
			},
		);

		return popup.open({
			correlation: this.args.correlationValue,
		});
	}
	<template>
		{{#if @isCalculating}}
			<span class="correlation-badge-loading">
				<LoaderSvgComponent />
			</span>
		{{else if this.showBadge}}
			<a
				role="button"
				class={{joinClasses
					(array "correlation-badge" (if @isDisabled "correlation-badge--disabled"))
				}}
				{{on "click" this.openCorrelationPopup}}
				{{onHoverPopup this.openHoverPopup}}
			>
				<span class="correlation-label {{this.strength}}">{{this.strength}}</span>
			</a>
		{{/if}}
	</template>
}
